import { motion } from "framer-motion";
import {
  Bot,
  BrainCircuit,
  GitBranch,
  Sparkles,
  TrendingUp,
  Workflow,
} from "lucide-react";

const featureCards = [
  {
    icon: Workflow,
    title: "AI Automation",
    description:
      "Orchestrate repetitive work with trigger-based flows, autonomous approvals, and live observability baked in.",
    metric: "80% less manual routing",
    color: "#22D3EE",
    tags: ["Adaptive agents", "Autonomous triggers"],
    layout: "lg:col-span-2 lg:translate-y-8",
  },
  {
    icon: Bot,
    title: "Smart Chatbots",
    description:
      "Deploy conversational copilots grounded in your knowledge base with escalation logic that feels seamless.",
    metric: "24/7 intelligent support",
    color: "#6366F1",
    tags: ["Knowledge search", "Agent handoff"],
    layout: "lg:col-span-2",
  },
  {
    icon: TrendingUp,
    title: "Predictive Analytics",
    description:
      "Forecast demand, surface anomalies, and trigger smarter decisions from real-time behavioral and operational data.",
    metric: "Real-time foresight",
    color: "#A78BFA",
    tags: ["Trend scoring", "Decision engine"],
    layout: "lg:col-span-2 lg:-translate-y-6",
  },
  {
    icon: GitBranch,
    title: "Workflow Integration",
    description:
      "Unify CRMs, internal tools, APIs, and human checkpoints so AI automations can move through your stack without friction.",
    metric: "Cross-platform sync",
    color: "#60A5FA",
    tags: ["API connectors", "Event pipelines"],
    layout: "lg:col-span-3 lg:-translate-y-2",
  },
  {
    icon: BrainCircuit,
    title: "Neural Processing",
    description:
      "Run classification, reasoning, summarization, and orchestration through a glowing neural layer tuned for business workflows.",
    metric: "Multi-model execution",
    color: "#F472B6",
    tags: ["Model routing", "Reasoning loops"],
    layout: "lg:col-span-3 lg:translate-y-10",
  },
];

const sectionParticles = Array.from({ length: 18 }, (_, index) => ({
  id: index,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  size: 2 + Math.random() * 3,
  delay: Math.random() * 5,
  duration: 10 + Math.random() * 7,
  color:
    index % 2 === 0
      ? "rgba(34, 211, 238, 0.45)"
      : "rgba(167, 139, 250, 0.4)",
}));

const FeatureCards = () => {
  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.08),transparent_28%),radial-gradient(circle_at_78%_40%,rgba(167,139,250,0.12),transparent_24%),linear-gradient(180deg,rgba(7,12,27,0.95),rgba(9,14,30,0.92))]" />
      <div className="absolute inset-0 tech-grid-dense opacity-35" />
      <div className="absolute left-1/2 top-0 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.16),transparent_70%)] blur-[120px]" />

      {sectionParticles.map((particle) => (
        <div
          key={particle.id}
          className="pointer-events-none absolute rounded-full"
          style={{
            left: particle.left,
            top: particle.top,
            width: particle.size,
            height: particle.size,
            background: particle.color,
            boxShadow: `0 0 ${particle.size * 4}px ${particle.color}`,
            animation: `particle-float ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
          }}
        />
      ))}

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="ai-badge mb-5 inline-flex">
            <Sparkles className="h-3.5 w-3.5 text-ai-glow" />
            Floating AI Capability Cards
          </span>
          <h2
            className="text-3xl font-bold text-white sm:text-4xl lg:text-5xl"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            Glassmorphism modules built for a{" "}
            <span className="gradient-text">futuristic automation stack</span>
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-400">
            Each card represents a deployable AI building block designed to glow, float,
            and fit into the same modern orchestration layer.
          </p>
        </motion.div>

        <div className="mt-16 grid gap-6 lg:grid-cols-6">
          {featureCards.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: index * 0.08, duration: 0.55 }}
              whileHover={{ y: -10, scale: 1.01 }}
              className={`group relative ${feature.layout}`}
            >
              <div
                className="absolute inset-0 rounded-[30px] opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
                style={{
                  background: `radial-gradient(circle at top, ${feature.color}35, transparent 62%)`,
                }}
              />

              <div
                className="relative h-full overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(165deg,rgba(7,12,27,0.94),rgba(16,24,47,0.78))] p-7 shadow-[0_30px_80px_rgba(2,6,23,0.45)] backdrop-blur-2xl sm:p-8"
                style={{
                  animation: `float-slow ${6.5 + index * 0.7}s ease-in-out ${index * 0.35}s infinite`,
                }}
              >
                <div
                  className="absolute inset-0 opacity-70"
                  style={{
                    background: `radial-gradient(circle at top right, ${feature.color}18, transparent 36%)`,
                  }}
                />
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${feature.color}, transparent)`,
                  }}
                />

                <div className="relative z-10 flex items-start justify-between gap-4">
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-2xl"
                    style={{
                      background: `${feature.color}18`,
                      border: `1px solid ${feature.color}40`,
                      boxShadow: `0 0 24px ${feature.color}12`,
                    }}
                  >
                    <feature.icon
                      className="h-7 w-7"
                      style={{
                        color: feature.color,
                        filter: `drop-shadow(0 0 10px ${feature.color})`,
                      }}
                    />
                  </div>

                  <span
                    className="rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-[0.26em] uppercase"
                    style={{
                      color: feature.color,
                      borderColor: `${feature.color}40`,
                      background: `${feature.color}10`,
                    }}
                  >
                    {feature.metric}
                  </span>
                </div>

                <h3
                  className="mt-8 text-2xl font-semibold text-white sm:text-[2rem]"
                  style={{ fontFamily: '"Space Grotesk", sans-serif' }}
                >
                  {feature.title}
                </h3>

                <p className="mt-4 text-[0.96rem] leading-7 text-slate-400">
                  {feature.description}
                </p>

                <div className="mt-6 flex flex-wrap gap-2.5">
                  {feature.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium tracking-[0.18em] text-slate-300 uppercase"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-8 flex items-center gap-2 text-sm font-medium text-slate-300">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{
                      background: feature.color,
                      boxShadow: `0 0 12px ${feature.color}`,
                    }}
                  />
                  Orbiting inside your AI command center
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="section-divider mt-20" />
    </section>
  );
};

export default FeatureCards;
